<template>
  <ValidationObserver
    ref="observer"
    v-slot="{ invalid }"
    tag="div"
  >
    <ModalConfirmation
      :class="$style.modal"
      :data-test-id="$testId('modalConfirmation')"
      :isDisabledPrimary="invalidDate || shouldDisablePrimary
        || invalid && settings.emailSettings.sendWelcomeEmail"
      :showModal="showModal"
      title="Activate User"
      @cancel="closeModal"
      @close="closeModal"
      @save="updateUser"
    >
      <template v-slot:body>
        <ScheduleNotification
          inAsidePanel
          :notificationAction="$options.activate"
        />
        <ParagraphBody
          :data-test-id="$testId('description')"
        >
          Activating an account will give a user access to all JumpCloud provided
          resources. Accounts with scheduled activations will not be granted
          access until the date specified.
        </ParagraphBody>
        <SegmentedButton
          :class="$style.activationButtons"
          name="activationButtons"
          :options="$options.activationOptions"
          :value="activationChoice"
          @input="updateActivationChoice"
        />
        <FieldsScheduleActivation
          v-model="settings"
          :action="$options.activate"
          :class="$style.fieldContainer"
          :data-test-id="$testId('fieldsScheduleActivation')"
          displayInline
          :inMainPanel="false"
          :showDate="showDate"
          @dateIsInvalid="handleDateIsInvalid"
        />
      </template>
      <template v-slot:footerAddition>
        <Link
          :href="$options.kbLink"
          isExternal
          target="_blank"
          text="Learn how to customize email templates"
        />
      </template>
    </ModalConfirmation>
  </ValidationObserver>
</template>

<script>
import {
  Link,
  ModalConfirmation,
  ParagraphBody,
  SegmentedButton,
} from '@jumpcloud/ui-components';
import { ValidationObserver } from 'vee-validate3';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { userStateActionType } from 'client/systemusers/constants/SystemUserConstants';
import Constants, { ModuleConsts } from 'client/util/Constants';
import FieldsScheduleActivation from 'client/systemusers/components/FieldsScheduleActivation.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalHelper from 'client/systemusers/Modals/ModalHelper';
import ScheduleNotification from 'client/systemusers/components/ScheduleNotification.vue';

const {
  activationOptionTypes,
  generateActivationNotification,
  setDefaultActionDate,
} = ModalHelper;

const { error, success } = Constants;
const { activated } = ModuleConsts.systemUser.accountStates;

export default {
  name: 'ActivateUserModal',

  components: {
    FieldsScheduleActivation,
    Link,
    ModalConfirmation,
    ParagraphBody,
    ScheduleNotification,
    SegmentedButton,
    ValidationObserver,
  },

  props: {
    item: {
      type: Object,
      default: undefined,
    },

    showModal: Boolean,
  },

  data() {
    return {
      activationChoice: this.$options.schedule,
      settings: {
        emailSettings: {
          sendWelcomeEmail: true,
          welcomeEmailAddress: '',
        },
        eventDate: setDefaultActionDate(userStateActionType.activate),
      },
      isDateInvalid: false,
      shouldDisablePrimary: false,
    };
  },

  computed: {
    ...mapGetters('SystemUserModel', {
      userDisplayName: 'displayName',
    }),

    invalidDate() {
      return this.showDate && this.isDateInvalid;
    },

    showDate() {
      return this.activationChoice === this.$options.schedule;
    },
  },

  accountStates: ModuleConsts.systemUser.accountStates,

  activationOptions: [
    // These need to be in this order for the button group
    activationOptionTypes.schedule,
    activationOptionTypes.now,
  ],

  activate: userStateActionType.activate,

  kbLink: KbArticleUrls.emailCustomization,

  now: activationOptionTypes.now.value,
  schedule: activationOptionTypes.schedule.value,

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    ...mapActions('SystemUserCollection', [
      'scheduleUserStateChange',
      'updateModifiedItemCount',
      'updateNextScheduledEvent',
    ]),

    ...mapActions('SystemUserModel', [
      'activateUser',
      'activateWithEmail',
    ]),

    closeModal() {
      this.$emit('close');
    },

    handleDateIsInvalid(value) {
      this.isDateInvalid = value;
    },

    /**
     * Display notification to admin depending on action
     */
    notifyAdmin(type, option) {
      const { message, title } = generateActivationNotification(type, option, this.userDisplayName);

      if (type === success) {
        this.triggerSuccessNotification({ title, message });
      } else {
        this.triggerErrorNotification({ title, message });
      }
    },

    updateActivationChoice(value) {
      this.activationChoice = value;
      this.shouldDisablePrimary = value === this.$options.now
        ? this.item.state === activated
        : false;
    },

    /**
     * Schedule Activation: Create a scheduled event for the user
     * Activate now: Immediately activate the user
     */
    async updateUser() {
      if (this.activationChoice === this.$options.schedule) {
        const results = await this.scheduleUserStateChange({
          payload: {
            action: this.$options.activate,
            emailSettings: this.settings.emailSettings,
            eventDate: this.settings.eventDate,
            ids: [this.item._id],
          },
        });

        const { failedJobs, scheduledUsers } = results;

        if (scheduledUsers.length) {
          // Update scheduled events list so that event badge displays
          this.updateNextScheduledEvent([this.item._id]);
          this.notifyAdmin(success, this.$options.schedule);
        } else if (failedJobs.length) {
          this.notifyAdmin(error, this.$options.schedule);
        }
      } else if (this.activationChoice === this.$options.now) {
        try {
          const { emailSettings } = this.settings;
          await this.activateUser();

          if (emailSettings.sendWelcomeEmail) {
            await this.activateWithEmail(emailSettings.welcomeEmailAddress);
          }
          this.notifyAdmin(success, this.$options.now);
        } catch (err) {
          this.notifyAdmin(error, this.$options.now);
        }
      }

      // Let any components watching know that this user has been updated
      this.updateModifiedItemCount();
      this.closeModal();
    },
  },
};
</script>

<style module scoped>
.modal.modal {
  --modal-width: 67ch;
  --modal-max-width: 67ch;
}

.notification {
  margin-bottom: var(--jc-spacer-medium);
}

/* Reach in and set the max-width so that the button text doesn't wrap */
.activationButtons /deep/ div {
  max-width: 50ch;
}

.fieldContainer {
  display: grid;
  min-height: var(--jc-size-14);
}
</style>
